import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { franquiaService } from "../../services/franquiaService";
import { Franquia, Membro } from "../../types/franquia";
import { useAuth } from "../../contexts/AuthContext";
import { TerritoryDetailsModal } from "../../components/TerritoryDetailsModal";

// Define a type for the region data passed to the modal
// This makes it compatible with both Franquia and CodigoValidacao types
interface RegiaoParaModal {
  municipio: {
    id: number;
    nome: string;
    nome_original?: string;
    uf?: { sigla: string };
  };
  bairros: { id: number; nome_original: string }[];
}

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  membro: Membro | null;
  actionType: "delete" | "setAsAdmin";
  adminContato: { email: string; telefone: string } | null;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  membro,
  actionType,
  adminContato,
}) => {
  const [confirmations, setConfirmations] = useState([false, false, false]);

  if (!isOpen) return null;

  const handleConfirmationChange = (index: number) => {
    const newConfirmations = [...confirmations];
    newConfirmations[index] = !newConfirmations[index];
    setConfirmations(newConfirmations);
  };

  const allConfirmed = confirmations.every((c) => c);

  const actionText =
    actionType === "delete" ? "deletar" : "definir como administrador";
  const titleText =
    actionType === "delete" ? "Deletar Membro" : "Definir como Administrador";

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3 text-center">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {titleText}
          </h3>
          <div className="mt-2 px-7 py-3">
            <p className="text-sm text-gray-500">
              Você está prestes a {actionText} o usuário{" "}
              {membro?.user.nome_completo}. Esta é uma ação irreversível.
            </p>
            <div className="mt-4 text-left">
              <p className="text-sm font-medium text-gray-700">
                Por favor, entre em contato com o administrador atual da
                franquia para confirmar esta ação:
              </p>
              <p className="text-sm text-gray-500">
                Email: {adminContato?.email}
              </p>
              <p className="text-sm text-gray-500">
                Telefone: {adminContato?.telefone}
              </p>
            </div>
            <div className="mt-4 space-y-2 text-left">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={confirmations[0]}
                  onChange={() => handleConfirmationChange(0)}
                />
                <span className="ml-2 text-sm">
                  Eu entendo que esta ação é irreversível.
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={confirmations[1]}
                  onChange={() => handleConfirmationChange(1)}
                />
                <span className="ml-2 text-sm">
                  Eu confirmo que contactei o administrador.
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={confirmations[2]}
                  onChange={() => handleConfirmationChange(2)}
                />
                <span className="ml-2 text-sm">
                  Eu desejo prosseguir com a ação.
                </span>
              </label>
            </div>
          </div>
          <div className="items-center px-4 py-3">
            <button
              onClick={onConfirm}
              disabled={!allConfirmed}
              className={`px-4 py-2 bg-red-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 ${
                !allConfirmed && "opacity-50 cursor-not-allowed"
              }`}
            >
              Confirmar
            </button>
            <button
              onClick={onClose}
              className="mt-2 px-4 py-2 bg-gray-200 text-gray-800 text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const DetalhesFranquia: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isTactUser } = useAuth();
  const [franquia, setFranquia] = useState<Franquia | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    membro: Membro | null;
    actionType: "delete" | "setAsAdmin";
  }>({ isOpen: false, membro: null, actionType: "delete" });
  const [territoryModalState, setTerritoryModalState] = useState<{
    isOpen: boolean;
    regiao: RegiaoParaModal | null;
  }>({ isOpen: false, regiao: null });

  const isAdmin = (membro: Membro) => membro.cargos.includes("Franquia Admin");
  const franquiaAdmin = franquia?.membros.find(isAdmin);

  const handleOpenModal = (
    membro: Membro,
    actionType: "delete" | "setAsAdmin"
  ) => {
    setModalState({ isOpen: true, membro, actionType });
  };

  const handleCloseModal = () => {
    setModalState({ isOpen: false, membro: null, actionType: "delete" });
  };

  const handleConfirmAction = async () => {
    if (!modalState.membro || !id) return;

    try {
      if (modalState.actionType === "delete") {
        await franquiaService.removerMembro(
          parseInt(id),
          modalState.membro.id_membro_franquia
        );
        // Atualizar o estado para refletir a remoção
        setFranquia((prev) =>
          prev
            ? {
                ...prev,
                membros: prev.membros.filter(
                  (m) =>
                    m.id_membro_franquia !==
                    modalState.membro?.id_membro_franquia
                ),
              }
            : null
        );
      } else {
        await franquiaService.definirNovoAdmin(
          parseInt(id),
          modalState.membro.user.id
        );
        // Atualizar o estado para refletir a mudança de admin
        // (pode ser necessário recarregar os dados da franquia)
        const franquiaData = await franquiaService.buscarPorId(parseInt(id));
        setFranquia(franquiaData);
      }
    } catch (err) {
      console.error(
        `Erro ao ${
          modalState.actionType === "delete" ? "deletar" : "definir como admin"
        }:`,
        err
      );
      setError(
        `Erro ao ${
          modalState.actionType === "delete" ? "deletar" : "definir como admin"
        }`
      );
    }

    handleCloseModal();
  };

  useEffect(() => {
    const fetchFranquia = async () => {
      if (!id) {
        setError("ID da franquia não fornecido");
        setLoading(false);
        return;
      }

      try {
        const franquiaData = await franquiaService.buscarPorId(parseInt(id));
        setFranquia(franquiaData);
      } catch (err) {
        console.error("Erro ao carregar franquia:", err);
        setError("Erro ao carregar dados da franquia");
      } finally {
        setLoading(false);
      }
    };

    fetchFranquia();
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#32bef0]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <h3 className="text-sm font-medium text-red-800">{error}</h3>
      </div>
    );
  }

  if (!franquia) {
    return (
      <div className="rounded-md bg-yellow-50 p-4">
        <h3 className="text-sm font-medium text-yellow-800">
          Franquia não encontrada
        </h3>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto bg-white shadow sm:rounded-lg p-6">
      <ConfirmationModal
        isOpen={modalState.isOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmAction}
        membro={modalState.membro}
        actionType={modalState.actionType}
        adminContato={
          franquiaAdmin
            ? { email: franquiaAdmin.user.email, telefone: "N/A" }
            : null
        }
      />

      <TerritoryDetailsModal
        isOpen={territoryModalState.isOpen}
        onClose={() => setTerritoryModalState({ isOpen: false, regiao: null })}
        municipio={territoryModalState.regiao?.municipio || null}
        includedBairros={territoryModalState.regiao?.bairros || []}
      />

      <div className="border-b border-gray-200 pb-4 mb-6">
        <h1 className="text-2xl font-semibold text-[#29306a]">
          Detalhes da Franquia
        </h1>
        <p className="mt-1 text-sm text-[#64748b]">{franquia.nome_fantasia}</p>
      </div>

      <div>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-[#29306a]">Razão Social</dt>
            <dd className="mt-1 text-sm text-[#64748b]">
              {franquia.razao_social}
            </dd>
          </div>
          <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-[#29306a]">CNPJ</dt>
            <dd className="mt-1 text-sm text-[#64748b]">{franquia.cnpj}</dd>
          </div>
          <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-[#29306a]">
              Limite de Funcionários
            </dt>
            <dd className="mt-1 text-sm text-[#64748b]">
              {franquia.limite_funcionarios}
            </dd>
          </div>
          <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-[#29306a]">
              Data de Criação
            </dt>
            <dd className="mt-1 text-sm text-[#64748b]">
              {new Date(franquia.created).toLocaleDateString("pt-BR")}
            </dd>
          </div>
        </dl>
      </div>

      <div className="mt-8">
        <h2 className="text-xl font-semibold text-[#29306a] mb-4">
          Membros ({franquia.contagem_membros})
        </h2>
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-1/4 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Funcionário
                  </th>
                  <th className="w-1/4 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cargos
                  </th>
                  <th className="w-1/4 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="w-1/4 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Último Acesso
                  </th>
                  {isTactUser() && (
                    <th className="w-1/4 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {franquia.membros.map((membro) => (
                  <tr
                    key={membro.id_membro_franquia}
                    className="hover:bg-gray-50"
                  >
                    <td className="w-1/4 px-6 py-4">
                      <div className="text-sm font-medium text-gray-900 break-words">
                        {membro.user.nome_completo}
                      </div>
                      <div className="text-sm text-gray-500 break-words">
                        {membro.user.email}
                      </div>
                    </td>
                    <td className="w-1/4 px-6 py-4">
                      <div className="text-sm text-gray-900 break-words">
                        {membro.cargos.join(", ")}
                      </div>
                    </td>
                    <td className="w-1/4 px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          membro.status_na_franquia === "ATIVO"
                            ? "bg-green-100 text-green-800"
                            : membro.user.is_bloqueado
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {membro.user.is_bloqueado
                          ? "BLOQUEADO"
                          : membro.status_na_franquia}
                      </span>
                    </td>
                    <td className="w-1/4 px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {membro.user.last_login
                        ? new Date(membro.user.last_login).toLocaleDateString(
                            "pt-BR"
                          )
                        : "Nunca"}
                    </td>
                    {isTactUser() && (
                      <td className="w-1/4 px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {!isAdmin(membro) && (
                          <>
                            <button
                              onClick={() => handleOpenModal(membro, "delete")}
                              className="text-red-600 hover:text-red-900"
                            >
                              Deletar
                            </button>
                            <button
                              onClick={() =>
                                handleOpenModal(membro, "setAsAdmin")
                              }
                              className="ml-4 text-indigo-600 hover:text-indigo-900"
                            >
                              Definir como Admin
                            </button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <h2 className="text-xl font-semibold text-[#29306a] mb-4">
          Áreas de Atuação
        </h2>
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {franquia.area_atuacao ? (
            (() => {
              const areaAtuacao = franquia.area_atuacao as any;
              const municipiosCompletos =
                areaAtuacao.municipios_completos ||
                areaAtuacao.municipios ||
                [];

              // Processar bairros agrupados por município
              let cidadesParciais: Array<{ municipio: any; bairros: any[] }> =
                [];

              if (
                areaAtuacao.regiao_municipio &&
                areaAtuacao.regiao_municipio.length > 0
              ) {
                cidadesParciais = areaAtuacao.regiao_municipio;
              } else if (
                areaAtuacao.bairros &&
                areaAtuacao.bairros.length > 0
              ) {
                const bairrosPorMunicipio = new Map<number, any[]>();
                areaAtuacao.bairros.forEach((bairro: any) => {
                  const municipioId = bairro.municipio.id;
                  if (!bairrosPorMunicipio.has(municipioId)) {
                    bairrosPorMunicipio.set(municipioId, []);
                  }
                  bairrosPorMunicipio.get(municipioId)!.push(bairro);
                });

                cidadesParciais = Array.from(bairrosPorMunicipio.entries()).map(
                  ([_, bairros]) => ({
                    municipio: bairros[0].municipio,
                    bairros: bairros,
                  })
                );
              }

              // Calcular estatísticas por estado
              const estadosMap = new Map<
                string,
                {
                  sigla: string;
                  nome: string;
                  cidadesCompletas: number;
                  cidadesParciais: number;
                  totalBairros: number;
                }
              >();

              municipiosCompletos.forEach((cidade: any) => {
                const ufSigla = cidade.uf?.sigla || "N/A";
                const ufNome = cidade.uf?.nome || "Desconhecido";
                if (!estadosMap.has(ufSigla)) {
                  estadosMap.set(ufSigla, {
                    sigla: ufSigla,
                    nome: ufNome,
                    cidadesCompletas: 0,
                    cidadesParciais: 0,
                    totalBairros: 0,
                  });
                }
                estadosMap.get(ufSigla)!.cidadesCompletas++;
              });

              cidadesParciais.forEach((item) => {
                const ufSigla = item.municipio.uf?.sigla || "N/A";
                const ufNome = item.municipio.uf?.nome || "Desconhecido";
                if (!estadosMap.has(ufSigla)) {
                  estadosMap.set(ufSigla, {
                    sigla: ufSigla,
                    nome: ufNome,
                    cidadesCompletas: 0,
                    cidadesParciais: 0,
                    totalBairros: 0,
                  });
                }
                const estado = estadosMap.get(ufSigla)!;
                estado.cidadesParciais++;
                estado.totalBairros += item.bairros?.length || 0;
              });

              const estados = Array.from(estadosMap.values()).sort((a, b) => {
                const totalA = a.cidadesCompletas + a.cidadesParciais;
                const totalB = b.cidadesCompletas + b.cidadesParciais;
                return totalB - totalA;
              });

              const totalCidades =
                municipiosCompletos.length + cidadesParciais.length;
              const totalBairros = cidadesParciais.reduce(
                (sum, item) => sum + (item.bairros?.length || 0),
                0
              );
              const maxCidades = Math.max(
                ...estados.map((e) => e.cidadesCompletas + e.cidadesParciais),
                1
              );

              // Combinar todas as cidades para detalhes
              const todasCidades = [
                ...municipiosCompletos.map((cidade: any) => ({
                  id: cidade.id,
                  nome: cidade.nome_original || cidade.nome,
                  uf: cidade.uf?.sigla || "N/A",
                  tipo: "completa" as const,
                  bairros: null,
                  regiao: null,
                })),
                ...cidadesParciais.map((item) => ({
                  id: item.municipio.id,
                  nome: item.municipio.nome_original || item.municipio.nome,
                  uf: item.municipio.uf?.sigla || "N/A",
                  tipo: "parcial" as const,
                  bairros: item.bairros,
                  regiao: item,
                })),
              ].sort((a, b) => a.nome.localeCompare(b.nome));

              return (
                <>
                  {/* Cabeçalho com Resumo */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 border-b">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                          <svg
                            className="w-6 h-6 text-blue-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <div>
                            <span className="text-2xl font-bold text-gray-900">
                              {totalCidades}
                            </span>
                            <span className="text-sm text-gray-600 ml-1">
                              cidades
                            </span>
                          </div>
                        </div>
                        <div className="h-8 w-px bg-gray-300"></div>
                        <div className="flex items-center gap-2">
                          <svg
                            className="w-6 h-6 text-indigo-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          <div>
                            <span className="text-2xl font-bold text-gray-900">
                              {totalBairros}
                            </span>
                            <span className="text-sm text-gray-600 ml-1">
                              bairros
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                          <span className="text-sm text-gray-700">
                            {municipiosCompletos.length} completas
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                          <span className="text-sm text-gray-700">
                            {cidadesParciais.length} parciais
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Grid Principal */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
                    {/* Coluna Esquerda: Estados */}
                    <div className="lg:col-span-1 p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <svg
                          className="w-5 h-5 text-gray-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                          />
                        </svg>
                        Cobertura por Estado
                      </h3>
                      {estados.length > 0 ? (
                        <div className="space-y-4">
                          {estados.slice(0, 5).map((estado) => {
                            const totalCidadesEstado =
                              estado.cidadesCompletas + estado.cidadesParciais;
                            const porcentagem = Math.round(
                              (totalCidadesEstado / maxCidades) * 100
                            );

                            return (
                              <div key={estado.sigla} className="group">
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-gray-900 text-lg">
                                      {estado.sigla}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {estado.nome}
                                    </span>
                                  </div>
                                  <span className="text-sm font-medium text-gray-700">
                                    {totalCidadesEstado}{" "}
                                    {totalCidadesEstado === 1
                                      ? "cidade"
                                      : "cidades"}
                                  </span>
                                </div>
                                <div className="relative">
                                  <div className="h-6 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-500 ease-out flex items-center justify-end pr-2"
                                      style={{ width: `${porcentagem}%` }}
                                    >
                                      {porcentagem > 15 && (
                                        <span className="text-xs font-semibold text-white">
                                          {porcentagem}%
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {porcentagem <= 15 && (
                                    <span className="absolute right-2 top-0 text-xs font-semibold text-gray-600">
                                      {porcentagem}%
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                  {estado.cidadesCompletas > 0 && (
                                    <span className="flex items-center gap-1">
                                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                      {estado.cidadesCompletas} completa
                                      {estado.cidadesCompletas > 1 ? "s" : ""}
                                    </span>
                                  )}
                                  {estado.cidadesParciais > 0 && (
                                    <span className="flex items-center gap-1">
                                      <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                                      {estado.cidadesParciais} parcial
                                      {estado.cidadesParciais > 1 ? "is" : ""}
                                    </span>
                                  )}
                                  {estado.totalBairros > 0 && (
                                    <span>• {estado.totalBairros} bairros</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          {estados.length > 5 && (
                            <div className="pt-2 text-center">
                              <span className="text-sm text-gray-500">
                                + {estados.length - 5} outros estados
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">
                          Nenhum estado com cobertura.
                        </p>
                      )}
                    </div>

                    {/* Coluna Direita: Detalhes das Cidades */}
                    <div className="lg:col-span-2 p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                          <svg
                            className="w-5 h-5 text-gray-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M4 6h16M4 10h16M4 14h16M4 18h16"
                            />
                          </svg>
                          Cidades ({todasCidades.length})
                        </h3>
                      </div>

                      {todasCidades.length > 0 ? (
                        <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                          {todasCidades.map((cidade) => (
                            <div
                              key={cidade.id}
                              className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
                            >
                              <div className="flex items-center gap-3 flex-1">
                                {cidade.tipo === "completa" ? (
                                  <div
                                    className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center"
                                    title="Cobertura completa"
                                  >
                                    <svg
                                      className="w-5 h-5 text-green-600"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </div>
                                ) : (
                                  <div
                                    className="flex-shrink-0 w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center"
                                    title="Cobertura parcial"
                                  >
                                    <svg
                                      className="w-5 h-5 text-yellow-600"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-gray-900 truncate">
                                      {cidade.nome}
                                    </span>
                                    <span className="flex-shrink-0 text-xs font-semibold text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                                      {cidade.uf}
                                    </span>
                                  </div>
                                  {cidade.tipo === "parcial" &&
                                    cidade.bairros && (
                                      <span className="text-xs text-gray-500">
                                        {cidade.bairros.length}{" "}
                                        {cidade.bairros.length === 1
                                          ? "bairro"
                                          : "bairros"}
                                      </span>
                                    )}
                                  {cidade.tipo === "completa" && (
                                    <span className="text-xs text-green-600 font-medium">
                                      100% de cobertura
                                    </span>
                                  )}
                                </div>
                              </div>

                              {cidade.tipo === "parcial" && cidade.regiao && (
                                <button
                                  onClick={() =>
                                    setTerritoryModalState({
                                      isOpen: true,
                                      regiao: cidade.regiao,
                                    })
                                  }
                                  className="flex-shrink-0 ml-3 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors flex items-center gap-1"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                    />
                                  </svg>
                                  Ver
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-8">
                          Nenhuma cidade cadastrada.
                        </p>
                      )}
                    </div>
                  </div>
                </>
              );
            })()
          ) : (
            <div className="p-8 text-center">
              <svg
                className="w-16 h-16 text-gray-300 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                />
              </svg>
              <p className="text-gray-500">Nenhuma área de atuação definida.</p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-8 flex justify-between items-center">
        <Link
          to="/franquias"
          className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#32bef0]"
        >
          Voltar
        </Link>
        {isTactUser() && (
          <div className="flex space-x-4">
            <Link
              to={`/franquias/${id}/areas-atuacao`}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto"
            >
              Gerenciar Áreas de Atuação
            </Link>
            <Link
              to={`/franquias/${id}/editar-detalhes`}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-[#32bef0] px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#2ba8d8] focus:outline-none focus:ring-2 focus:ring-[#32bef0] focus:ring-offset-2 sm:w-auto"
            >
              Editar Detalhes
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};
